import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import Seo from '../components/Seo';
import Image from '../components/common/Image';
import OtpLogin from '../components/common/OtpLogin';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useUI } from '../context/UIContext';
import { money } from '../utils/format';
import { payForOrder, loadRazorpay } from '../utils/razorpay';

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const { toast } = useUI();
  const navigate = useNavigate();
  const symbol = settings.shop?.currencySymbol || '$';
  const freeOver = Number(settings.shop?.freeShippingOver || 0);
  const shipping = freeOver && subtotal >= freeOver ? 0 : Number(settings.shop?.shippingFlatRate || 0);
  const defaultAddr = user?.addresses?.find((a) => a.isDefault) || user?.addresses?.[0] || {};
  const [addr, setAddr] = useState({ fullName: user?.name || defaultAddr.fullName || '', phone: user?.phone || defaultAddr.phone || '', email: user?.email || '', line1: defaultAddr.line1 || '', line2: defaultAddr.line2 || '', city: defaultAddr.city || '', state: defaultAddr.state || '', postalCode: defaultAddr.postalCode || '', country: defaultAddr.country || 'India' });
  const [payment, setPayment] = useState('cod');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [guest, setGuest] = useState(false);
  const rzEnabled = !!settings.payments?.razorpay?.enabled && !!settings.payments?.razorpay?.keyId;
  const codEnabled = settings.payments?.cod?.enabled !== false && settings.shop?.codEnabled !== false;

  // Preload the gateway script so the modal opens instantly when the order is placed.
  useEffect(() => {
    if (rzEnabled) loadRazorpay();
  }, [rzEnabled]);

  const methods = [rzEnabled && 'razorpay', codEnabled && 'cod'].filter(Boolean);
  const noMethods = methods.length === 0;

  // Keep the selection on a method that is actually offered.
  useEffect(() => {
    if (!methods.length) return;
    setPayment((p) => (methods.includes(p) ? p : methods[0]));
  }, [rzEnabled, codEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Binds an address field to state: <input {...f('city')} />
  const f = (k) => ({ value: addr[k], onChange: (e) => setAddr({ ...addr, [k]: e.target.value }) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post('/orders', { items: items.map((i) => ({ productId: i.productId, variantId: i.variantId, quantity: i.quantity })), shippingAddress: addr, paymentMethod: payment, note });
      const { order } = r;
      // The order exists either way; payment only decides whether it lands paid or unpaid.
      if (payment === 'razorpay') {
        const { paid } = await payForOrder({ order, settings, onToast: toast });
        clear();
        navigate(`/order/${order._id}?placed=1${paid ? '&paid=1' : '&unpaid=1'}`);
        return;
      }
      clear();
      navigate(`/order/${order._id}?placed=1`);
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container-fluid section-my text-center py-16">
        <Seo title="Checkout" noIndex />
        <p className="text-lg text-color-secondary mb-6">Your cart is empty.</p>
        <Link to="/collections/all" className="sf__btn sf__btn-primary">Continue shopping</Link>
      </div>
    );
  }

  return (
    <>
      <Seo title="Checkout" noIndex />
      <div className="container-fluid section-my">
        <h1 className="text-3xl md:text-4xl text-center mb-10">Checkout</h1>
        {!user && !guest && (
          <div className="max-w-md mx-auto eg-card mb-10">
            <OtpLogin title="Log in to check out faster" subtitle="Use your WhatsApp number – we'll save your address and orders." />
            <button type="button" className="sf__btn sf__btn-secondary w-full mt-4" onClick={() => setGuest(true)}>Continue as guest</button>
          </div>
        )}
        {(user || guest) && (
          <form onSubmit={submit} className="flex flex-wrap -mx-4">
            <div className="w-full lg:w-7/12 px-4">
              <h3 className="text-xl font-medium mb-4">Shipping address</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <input className="form-control" placeholder="Full name *" required {...f('fullName')} />
                <input className="form-control" placeholder="Phone / WhatsApp *" required {...f('phone')} />
                <input className="form-control md:col-span-2" type="email" placeholder="Email (for order updates)" {...f('email')} />
                <input className="form-control md:col-span-2" placeholder="Address line 1 *" required {...f('line1')} />
                <input className="form-control md:col-span-2" placeholder="Address line 2 (apartment, landmark)" {...f('line2')} />
                <input className="form-control" placeholder="City *" required {...f('city')} />
                <input className="form-control" placeholder="State" {...f('state')} />
                <input className="form-control" placeholder="Postal code" {...f('postalCode')} />
                <input className="form-control" placeholder="Country *" required {...f('country')} />
              </div>
              <h3 className="text-xl font-medium mt-8 mb-4">Payment</h3>
              <div className="space-y-3">
                {rzEnabled && (
                  <label className={`eg-radio ${payment === 'razorpay' ? 'active' : ''}`}>
                    <input type="radio" name="pm" checked={payment === 'razorpay'} onChange={() => setPayment('razorpay')} />
                    <span>
                      <strong>Pay now</strong>
                      <small>UPI, cards, net banking and wallets — secured by Razorpay</small>
                    </span>
                  </label>
                )}
                {codEnabled && (
                  <label className={`eg-radio ${payment === 'cod' ? 'active' : ''}`}>
                    <input type="radio" name="pm" checked={payment === 'cod'} onChange={() => setPayment('cod')} />
                    <span><strong>{settings.payments?.cod?.label || 'Cash on Delivery'}</strong><small>Pay when your order arrives</small></span>
                  </label>
                )}
                {noMethods && (
                  <p className="eg-checkout-nomethod">
                    No payment method is available right now. Please contact us to complete your order.
                  </p>
                )}
              </div>
              <textarea className="form-control w-full mt-6" rows={3} placeholder="Order notes (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="w-full lg:w-5/12 px-4 mt-10 lg:mt-0">
              <div className="eg-summary">
                <h3 className="text-xl font-medium mb-4">Your order</h3>
                {items.map((it) => (
                  <div key={`${it.productId}:${it.variantId}`} className="flex items-center py-3 border-b">
                    <div className="w-14 flex-shrink-0 relative"><Image src={it.image} alt={it.title} ratio={1} /><span className="eg-qty-badge">{it.quantity}</span></div>
                    <div className="flex-1 px-3 text-sm"><div className="font-medium">{it.title}</div>{it.variantTitle && <div className="text-color-secondary">{it.variantTitle}</div>}</div>
                    <div className="text-sm whitespace-nowrap">{money(it.price * it.quantity, symbol)}</div>
                  </div>
                ))}
                <div className="flex justify-between py-2 mt-2"><span>Subtotal</span><span>{money(subtotal, symbol)}</span></div>
                <div className="flex justify-between py-2 border-b"><span>Shipping</span><span>{shipping === 0 ? 'Free' : money(shipping, symbol)}</span></div>
                <div className="flex justify-between py-3 text-lg font-medium"><span>Total</span><span>{money(subtotal + shipping, symbol)}</span></div>
                <button type="submit" className="sf__btn sf__btn-primary w-full mt-2" disabled={busy || noMethods}>
                  {busy ? 'Processing…' : payment === 'razorpay' ? `Pay ${money(subtotal + shipping, symbol)}` : 'Place order'}
                </button>
                <p className="text-xs text-color-secondary mt-3">By placing your order you agree to our <Link to="/pages/terms-of-service" className="underline">terms of service</Link>.</p>
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
